import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Choice, Pill, Group, FIELD, slug } from "@/components/ecommerce/EcomFormParts";

export const SERVICE_CHOICES = ["eCommerce Website Development", "eCommerce Strategy & Growth", "Marketplace Enablement",
  "Improve an Existing Online Store", "Several of the Above", "Not Sure Yet"];
export const MARKETPLACE_CHOICES = ["Amazon", "Takealot", "Makro", "Multiple marketplaces", "Not sure"];
const CHANNELS = ["Physical store", "Own website or online store", "Marketplaces", "Social media", "WhatsApp", "Wholesale / B2B", "Not selling yet"];
const PRODUCT_COUNTS = ["1–20", "21–100", "101–500", "500+", "Not sure yet"];
const CHALLENGES = ["Not sure where to start", "Store isn't ready to sell", "Struggling with marketplaces", "Traffic without enough purchases",
  "Disconnected tools and processes", "Limited visibility into performance", "Something else"];
const TIMEFRAMES = ["As soon as possible", "Within 1 month", "1–3 months", "3–6 months", "Not sure yet"];

export function StepNeed({ form, set }) {
  return (
    <div className="space-y-7">
      <Group legend="Choose the service closest to your goal*">
        <div className="grid sm:grid-cols-2 gap-2.5">
          {SERVICE_CHOICES.map((o) => <Choice key={o} label={o} active={form.service === o} onClick={() => set("service", o)} testid={`ecom-form-service-${slug(o)}`} />)}
        </div>
      </Group>
      {form.service === "Marketplace Enablement" && (
        <Group legend="Which marketplace?*">
          <div className="flex flex-wrap gap-2" data-testid="ecom-form-marketplace-group">
            {MARKETPLACE_CHOICES.map((o) => <Pill key={o} label={o} active={form.marketplace === o} onClick={() => set("marketplace", o)} testid={`ecom-form-marketplace-${slug(o)}`} />)}
          </div>
        </Group>
      )}
    </div>
  );
}

export function StepBusiness({ form, set, toggle }) {
  return (
    <div className="space-y-7">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid="ecom-form-company" aria-label="Business name" className={FIELD} placeholder="Business name*" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} autoComplete="organization" />
        <Input data-testid="ecom-form-website" aria-label="Existing website URL" className={FIELD} type="text" inputMode="url" placeholder="Existing website URL (if any)" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} autoComplete="url" />
      </div>
      <Group legend="Current sales channels*" hint="(select all that apply)">
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((o) => <Pill key={o} label={o} active={form.channels.includes(o)} onClick={() => toggle("channels", o)} testid={`ecom-form-channel-${slug(o)}`} />)}
        </div>
      </Group>
      <Group legend="Approximate number of products*">
        <div className="flex flex-wrap gap-2">
          {PRODUCT_COUNTS.map((o) => <Pill key={o} label={o} active={form.products === o} onClick={() => set("products", o)} testid={`ecom-form-products-${slug(o) || "na"}`} />)}
        </div>
      </Group>
      <Group legend="Main eCommerce challenge*">
        <div className="grid sm:grid-cols-2 gap-2.5">
          {CHALLENGES.map((o) => <Choice key={o} label={o} active={form.challenge === o} onClick={() => set("challenge", o)} testid={`ecom-form-challenge-${slug(o)}`} />)}
        </div>
      </Group>
      <Group legend="Implementation timeframe*">
        <div className="flex flex-wrap gap-2">
          {TIMEFRAMES.map((o) => <Pill key={o} label={o} active={form.timeframe === o} onClick={() => set("timeframe", o)} testid={`ecom-form-timeframe-${slug(o)}`} />)}
        </div>
      </Group>
    </div>
  );
}

export function StepContact({ form, set }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid="ecom-form-first-name" aria-label="First name" className={FIELD} placeholder="First name*" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoComplete="given-name" />
        <Input data-testid="ecom-form-last-name" aria-label="Last name" className={FIELD} placeholder="Last name*" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="family-name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input data-testid="ecom-form-email" aria-label="Business email" className={FIELD} type="email" inputMode="email" placeholder="Business email*" value={form.business_email} onChange={(e) => set("business_email", e.target.value)} autoComplete="email" />
        <Input data-testid="ecom-form-mobile" aria-label="Mobile number" className={FIELD} type="tel" inputMode="tel" placeholder="Mobile number*" value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value)} autoComplete="tel" />
      </div>
      <Textarea data-testid="ecom-form-message" aria-label="Additional requirements" className={`${FIELD} h-auto min-h-[96px] py-3`} placeholder="Additional requirements (optional)" maxLength={2000} value={form.message} onChange={(e) => set("message", e.target.value)} />
      <input type="text" name="company_fax" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <label className="flex items-start gap-3 text-sm text-white/75 leading-relaxed cursor-pointer">
        <Checkbox data-testid="ecom-form-privacy-consent" checked={form.privacy} onCheckedChange={(v) => set("privacy", v === true)} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#E8562A] data-[state=checked]:border-[#E8562A]" />
        <span>I agree that Rekisa may use my information to assess my enquiry and contact me about it, as described in the{" "}
          <Link to="/privacy-policy" target="_blank" className="underline text-white hover:text-[#E8562A]" data-testid="ecom-form-privacy-link">Privacy Policy</Link>.*</span>
      </label>
      <label className="flex items-start gap-3 text-sm text-white/65 leading-relaxed cursor-pointer">
        <Checkbox data-testid="ecom-form-marketing-consent" checked={form.marketing} onCheckedChange={(v) => set("marketing", v === true)} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#E8562A] data-[state=checked]:border-[#E8562A]" />
        <span>Yes, send me occasional eCommerce insights and offers from Rekisa (optional — unsubscribe anytime).</span>
      </label>
    </div>
  );
}
